import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.services-dropdown')) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const services = [
    {
      icon: 'ri-instagram-line',
      title: 'Sosyal Medya Yönetimi',
      description: 'İçerik stratejisi ve topluluk yönetimi',
      path: '/hizmetler/sosyal-medya',
    },
    {
      icon: 'ri-advertisement-line',
      title: 'Meta & Google Reklamları',
      description: 'Hedef kitleye özel kampanyalar',
      path: '/hizmetler/dijital-reklamcilik',
    },
    {
      icon: 'ri-window-line',
      title: 'Web Tasarımı',
      description: 'CRM entegrasyonlu web siteleri',
      path: '/hizmetler/web-tasarim',
    },
    {
      icon: 'ri-database-2-line',
      title: 'CRM & Otomasyon',
      description: 'Süreç otomasyonu ve entegrasyonlar',
      path: '/hizmetler/crm-otomasyon',
    },
    {
      icon: 'ri-palette-line',
      title: 'Marka Kimliği',
      description: 'Kurumsal kimlik tasarımları',
      path: '/hizmetler/marka-kimligi',
    },
    {
      icon: 'ri-video-line',
      title: 'İçerik & Video Prodüksiyon',
      description: 'Video içerikler ve motion graphics',
      path: '/hizmetler/video-produksiyon',
    },
  ];

  const navLinks = [
    { name: 'Hakkımızda', path: '/hakkimizda' },
    { name: 'Paketler', path: '/paketler' },
    { name: 'Sigorta Dijital Akademi', path: '/akademi' },
    { name: 'İletişim', path: '/iletisim' },
  ];

  const scrollToContact = () => {
    if (location.pathname === '/iletisim') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.REACT_APP_NAVIGATE('/iletisim');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white ${
        isScrolled
          ? 'shadow-lg border-b border-gray-100'
          : 'shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 group">
            <img 
              src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/3b136704e4525f1e342631c59f008882.png" 
              alt="Sigorota360" 
              className="h-9 sm:h-11 transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <a
              href="/hakkimizda"
              className="font-poppins text-[15px] text-gray-700 hover:text-[#007dff] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Hakkımızda
            </a>
            
            {/* Services Dropdown */}
            <div className="relative services-dropdown">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsServicesOpen(!isServicesOpen);
                }}
                className="font-poppins text-[15px] text-gray-700 hover:text-[#007dff] transition-colors duration-300 whitespace-nowrap cursor-pointer flex items-center gap-1.5"
              >
                Hizmetler
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isServicesOpen && (
                <div 
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[720px] bg-white rounded-2xl shadow-2xl border border-gray-100 p-6"
                >
                  <div className="grid grid-cols-2 gap-3">
                    {services.map((service, index) => (
                      <Link
                        key={index}
                        to={service.path}
                        onClick={() => setIsServicesOpen(false)}
                        className="flex items-start gap-4 p-4 rounded-xl hover:bg-[#007dff]/5 transition-all duration-300 cursor-pointer group border border-transparent hover:border-[#007dff]/20"
                      >
                        <div className="w-12 h-12 flex items-center justify-center bg-[#007dff]/10 rounded-xl flex-shrink-0 group-hover:bg-[#007dff]/20 transition-colors duration-300">
                          <i className={`${service.icon} text-xl text-[#007dff]`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-poppins font-semibold text-[14px] text-gray-900 mb-1 group-hover:text-[#007dff] transition-colors duration-300">
                            {service.title}
                          </h4>
                          <p className="font-poppins text-[12px] text-gray-600 leading-relaxed">
                            {service.description}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="/paketler"
              className="font-poppins text-[15px] text-gray-700 hover:text-[#007dff] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Paketler
            </a>
            <a
              href="/sigorta-dijital-akademi"
              className="font-poppins text-[15px] text-gray-700 hover:text-[#007dff] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Sigorta Dijital Akademi
            </a>
            <a
              href="/iletisim"
              className="font-poppins text-[15px] text-gray-700 hover:text-[#007dff] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              İletişim
            </a>
          </nav>

          <a
            href="/teklif-al"
            className="hidden lg:block bg-[#007dff] text-white font-poppins font-semibold text-[15px] px-6 py-3 rounded-lg hover:bg-[#0066cc] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Teklif Al
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-11 h-11 flex items-center justify-center cursor-pointer rounded-xl hover:bg-gray-100 transition-colors duration-300"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl text-gray-800`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg overflow-y-auto max-h-[calc(100vh-80px)]">
          <nav className="flex flex-col px-4 py-4 gap-1.5 max-w-7xl mx-auto">
            <a
              href="/hakkimizda"
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-poppins text-base text-gray-700 hover:text-[#007dff] transition-colors duration-300 py-3 px-4 rounded-xl hover:bg-gray-50 cursor-pointer"
            >
              Hakkımızda
            </a>
            
            {/* Mobile Services Accordion */}
            <div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileServicesOpen(!isMobileServicesOpen);
                }}
                className={`w-full font-poppins text-base py-3.5 px-4 rounded-xl transition-all duration-300 flex items-center justify-between ${
                  location.pathname.startsWith('/hizmetler')
                    ? 'text-[#007dff] bg-[#007dff]/10 font-semibold' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Hizmetler
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isMobileServicesOpen && (
                <div className="mt-2 space-y-2 animate-fadeIn">
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setIsMobileServicesOpen(false);
                      }}
                      className="flex items-start gap-3 p-3.5 rounded-xl hover:bg-[#007dff]/5 transition-all cursor-pointer border border-transparent hover:border-[#007dff]/20"
                    >
                      <div className="w-9 h-9 flex items-center justify-center bg-[#007dff]/10 rounded-lg flex-shrink-0">
                        <i className={`${service.icon} text-base text-[#007dff]`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-poppins font-semibold text-[13px] text-gray-900 mb-1">
                          {service.title}
                        </h4>
                        <p className="font-poppins text-[11px] text-gray-600 leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/paketler"
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-poppins text-base text-gray-700 hover:text-[#007dff] transition-colors duration-300 py-3 px-4 rounded-xl hover:bg-gray-50 cursor-pointer"
            >
              Paketler
            </a>
            <a
              href="/sigorta-dijital-akademi"
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-poppins text-base text-gray-700 hover:text-[#007dff] transition-colors duration-300 py-3 px-4 rounded-xl hover:bg-gray-50 cursor-pointer"
            >
              Sigorta Dijital Akademi
            </a>
            <a
              href="/iletisim"
              onClick={() => setIsMobileMenuOpen(false)}
              className="font-poppins text-base text-gray-700 hover:text-[#007dff] transition-colors duration-300 py-3 px-4 rounded-xl hover:bg-gray-50 cursor-pointer"
            >
              İletişim
            </a>
            <a
              href="/teklif-al"
              onClick={() => setIsMobileMenuOpen(false)}
              className="bg-[#007dff] text-white font-poppins font-semibold text-base px-6 py-3 rounded-lg hover:bg-[#0066cc] transition-all duration-300 text-center whitespace-nowrap cursor-pointer mt-2"
            >
              Teklif Al
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
